/**
 * Parse OFX 1.x SGML (and basic XML) bank statements.
 * Optimized for Nubank OFX 1.02 exports.
 */

function parseOfxTimezoneOffset(dtPosted) {
  const tzMatch = String(dtPosted).match(/\[(-?\d+):/);
  if (!tzMatch) return 0;
  return parseInt(tzMatch[1], 10);
}

/**
 * @param {string} dtPosted e.g. "20260601000000[-3:BRT]"
 * @returns {number|null} UTC milliseconds
 */
export function parseOfxDate(dtPosted) {
  const match = String(dtPosted).match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const offsetHours = parseOfxTimezoneOffset(dtPosted);
  return Date.UTC(+y, +mo - 1, +d, +h, +mi, +s) - offsetHours * 3600000;
}

/** Start of local calendar day for a timestamp. */
export function startOfLocalDay(ms) {
  const date = new Date(ms);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function extractTag(block, tag) {
  const re = new RegExp(`<${tag}>([^<\\r\\n]*)`, 'i');
  const match = block.match(re);
  return match ? match[1].trim() : '';
}

function extractStmtTrnBlocks(content) {
  const blocks = [];
  const re = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/gi;
  let match;
  while ((match = re.exec(content)) !== null) {
    blocks.push(match[1]);
  }
  return blocks;
}

function extractMeta(content) {
  const org = extractTag(content, 'ORG');
  const acctId = extractTag(content, 'ACCTID');
  const acctType = extractTag(content, 'ACCTTYPE');
  const dtStart = extractTag(content, 'DTSTART');
  const dtEnd = extractTag(content, 'DTEND');
  const dtStartMs = dtStart ? parseOfxDate(dtStart) : null;
  const dtEndMs = dtEnd ? parseOfxDate(dtEnd) : null;
  return {
    org,
    acctId,
    acctType,
    dateRange: {
      from: dtStartMs != null ? startOfLocalDay(dtStartMs) : null,
      to: dtEndMs != null ? startOfLocalDay(dtEndMs) : null,
    },
  };
}

function normalizeAmountForAccount(rawAmount, accountType) {
  const amount = Math.round(Number(rawAmount) * 100) / 100;
  if (accountType === 'credit_card' && amount > 0) {
    return -amount;
  }
  return amount;
}

/**
 * @param {string} content Raw OFX file text
 * @param {{ accountType?: string }} options
 * @returns {{ transactions: object[], meta: object, errors: string[] }}
 */
export function parseOfx(content, options = {}) {
  const errors = [];
  if (!content || typeof content !== 'string') {
    return { transactions: [], meta: {}, errors: ['Arquivo vazio ou inválido.'] };
  }

  const trimmed = content.trim();
  const isXml = trimmed.startsWith('<?xml');
  const isSgml = /OFXHEADER:100/i.test(trimmed) || /DATA:OFXSGML/i.test(trimmed) || /<OFX>/i.test(trimmed);

  if (!isXml && !isSgml) {
    return { transactions: [], meta: {}, errors: ['Formato OFX não reconhecido.'] };
  }

  const meta = extractMeta(trimmed);
  const blocks = extractStmtTrnBlocks(trimmed);
  if (blocks.length === 0) {
    errors.push('Nenhuma transação encontrada no arquivo.');
  }

  const accountType = options.accountType || 'checking';
  const transactions = [];

  for (const block of blocks) {
    const dtPosted = extractTag(block, 'DTPOSTED');
    const trnAmt = extractTag(block, 'TRNAMT');
    const fitId = extractTag(block, 'FITID');
    const memo = extractTag(block, 'MEMO') || extractTag(block, 'NAME');
    const trnType = extractTag(block, 'TRNTYPE');

    const dateMs = parseOfxDate(dtPosted);
    const amount = Number(trnAmt);

    if (dateMs == null || isNaN(amount)) {
      errors.push(`Transação ignorada: data ou valor inválido (${fitId || memo || 'sem ID'}).`);
      continue;
    }

    transactions.push({
      date: startOfLocalDay(dateMs),
      amount: normalizeAmountForAccount(amount, accountType),
      description: memo || trnType || 'Transação',
      ofxFitId: fitId || null,
      trnType: trnType || null,
    });
  }

  return { transactions, meta, errors };
}
